import React, { useState, useEffect } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Table,
    Badge,
    Button,
    Progress,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    TabContent,
    TabPane,
    Form,
    FormGroup,
    Label,
    Input,
    Pagination,
    PaginationItem,
    PaginationLink,
    InputGroup,
    InputGroupAddon,
    InputGroupText,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Spinner as ReactstrapSpinner,
} from "reactstrap";
import { CSpinner as Spinner } from "@coreui/react";
import moment from "moment";
import {
    getReputationUsers,
    getAdminProfile,
    getRoleById,
    adjustReputationScore,
    adjustReputation,
    reverseReputationPenalty,
    getReputationHistory,
    flagUserReputation,
    unflagUserReputation,
    getReputationStats,
    applyReputationPenalty,
    toggleUnderReview,
    getReputationAudits,
    moderateReputationAudit
} from "../../api/ApiServices";
import CommonHeader from "components/Headers/CommonHeader.js";
import Swal from "sweetalert2";
import apiClient from "api/ApiClient.js";
import defaultAvatar from "assets/img/brand/avatar-2.jpg";

import DashboardUsersTab from "./DashboardUsersTab";
import AntiGamingAuditsTab from "./AntiGamingAuditsTab";
import EligibilityCheckerTab from "./EligibilityCheckerTab";
import { styles, getTierBadge, renderPaginationItems, getUserImage } from "./ReputationUtils";


const Reputation = () => {
    const [activeTab, setActiveTab] = useState("Dashboard & Users");
    const [isFlagging, setIsFlagging] = useState(false);

    const [users, setUsers] = useState([]);
    const [usersPage, setUsersPage] = useState(1);
    const [usersTotalPages, setUsersTotalPages] = useState(1);
    const [usersTotal, setUsersTotal] = useState(0);
    const [usersLimit, setUsersLimit] = useState(10);
    const [loading, setLoading] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    const [permissions, setPermissions] = useState({
        canAdjust: false,
        canFlag: false
    });

    const [filters, setFilters] = useState({
        tier: "",
        suspicious: "",
        minScore: "",
        maxScore: "",
        sort: ""
    });

    const [adjustModal, setAdjustModal] = useState(false);
    const [flagModal, setFlagModal] = useState(false);
    const [historyModal, setHistoryModal] = useState(false);
    const [adjustData, setAdjustData] = useState({ score: 0, reason: "" });
    const [historyData, setHistoryData] = useState({ history: [], snapshots: [] });
    const [flagReason, setFlagReason] = useState("");
    const [isAdjusting, setIsAdjusting] = useState(false);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const [badgePenalties, setBadgePenalties] = useState({
        connector: 0,
        mentor: 0,
        cross_country: 0,
        multi_language: 0
    });
    const [historyPage, setHistoryPage] = useState(1);
    const [historyTotalPages, setHistoryTotalPages] = useState(1);
    const [adjHistoryPage, setAdjHistoryPage] = useState(1);
    const [adjHistoryTotalPages, setAdjHistoryTotalPages] = useState(1);

    const [repStats, setRepStats] = useState(null);
    const [isBadgeModalOpen, setIsBadgeModalOpen] = useState(false);
    const [search, setSearch] = useState("");

    const [moderationModal, setModerationModal] = useState(false);
    const [moderationAudits, setModerationAudits] = useState([]);
    const [isModerating, setIsModerating] = useState(false);
    const [moderationLoading, setModerationLoading] = useState(false);
    const [moderationAuditsLoading, setModerationAuditsLoading] = useState(false);

    const toggleBadgeModal = () => setIsBadgeModalOpen(!isBadgeModalOpen);

    const loadRepStats = async () => {
        try {
            const res = await getReputationStats();
            if (res.status && res.payload) {
                setRepStats(res.payload);
            }
        } catch (err) {
            console.error("Failed to load rep stats:", err);
        }
    };

    const fetchPermissions = async () => {
        try {
            const profile = await getAdminProfile();
            const roleUuid = profile?.payload?.au_role;
            if (!roleUuid) {
                console.warn("No role UUID found in profile");
                return;
            }

            const roleData = await getRoleById(roleUuid);
            const role = roleData?.payload?.role;
            const actionPermissions = role?.r_action_permissions || [];
            const isSuperAdmin = role?.r_name === "Super Admin";

            setPermissions({
                canAdjust: isSuperAdmin || actionPermissions.some(ap => ap.ap_name === "Adjust Reputation Score" || ap.ap_key === "ADJUST_REPUTATION_SCORE"),
                canFlag: isSuperAdmin
            });
        } catch (err) {
            console.error("Permission fetch failed:", err);
        }
    };

    const loadHistory = async (userId, hPage = 1, sPage = 1) => {
        setIsHistoryLoading(true);
        try {
            const res = await getReputationHistory(userId, hPage, sPage, 5);
            if (res.status && res.payload) {
                setHistoryData({
                    history: res.payload.history.items,
                    snapshots: res.payload.snapshots.items
                });

                // Update Snapshot pagination
                setHistoryPage(res.payload.snapshots.page);
                setHistoryTotalPages(res.payload.snapshots.totalPages);

                // Update Adjustment History pagination
                setAdjHistoryPage(res.payload.history.page);
                setAdjHistoryTotalPages(res.payload.history.totalPages);
            }
        } catch (err) {
            console.error("Failed to load history:", err);
            Swal.fire("Error", "Failed to fetch reputation history", "error");
        } finally {
            setIsHistoryLoading(false);
        }
    };

    const renderPaginationItems = (currentPage, totalItems, limit, onPageChange) => {
        const totalPagesCount = Math.ceil(totalItems / limit);
        if (totalPagesCount <= 1) return null;

        const pages = [];
        const delta = 2;

        pages.push(
            <PaginationItem key={1} active={currentPage === 1}>
                <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(1); }}>1</PaginationLink>
            </PaginationItem>
        );

        if (currentPage > delta + 2) {
            pages.push(<PaginationItem key="start-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
        }

        const rangeStart = Math.max(2, currentPage - delta);
        const rangeEnd = Math.min(totalPagesCount - 1, currentPage + delta);

        for (let i = rangeStart; i <= rangeEnd; i++) {
            pages.push(
                <PaginationItem key={i} active={currentPage === i}>
                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(i); }}>{i}</PaginationLink>
                </PaginationItem>
            );
        }

        if (currentPage < totalPagesCount - delta - 1) {
            pages.push(<PaginationItem key="end-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
        }

        if (totalPagesCount > 1) {
            pages.push(
                <PaginationItem key={totalPagesCount} active={currentPage === totalPagesCount}>
                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(totalPagesCount); }}>{totalPagesCount}</PaginationLink>
                </PaginationItem>
            );
        }

        return pages;
    };

    const fetchUsers = async (page = 1, searchText = search, filterParams = filters) => {
        try {
            setLoading(true);

            const response = await getReputationUsers({
                page,
                limit: usersLimit,
                search: searchText,
                tier: filterParams.tier || "",
                suspicious: filterParams.suspicious || "",
                minScore: filterParams.minScore || "",
                maxScore: filterParams.maxScore || "",
                sort: filterParams.sort || ""
            });
            console.log("users response ====", response)

            if (response?.status === true && response?.payload?.users) {
                setUsers(response.payload.users);
                setUsersTotal(response.payload.total || 0);
                setUsersTotalPages(response.payload.totalPages || 1);
                setUsersPage(response.payload.page || 1);
            } else {
                setUsers([]);
                setUsersTotal(0);
                setUsersTotalPages(1);
            }
        } catch (err) {
            console.error(err);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delay = setTimeout(() => {
            fetchUsers(1, search, filters);
        }, 500);

        return () => clearTimeout(delay);
    }, [search, filters.tier, filters.suspicious, filters.sort]);

    useEffect(() => {
        fetchUsers(1, search, filters);
        fetchPermissions();
        loadRepStats();
    }, []);

    const [eligibilityRoom, setEligibilityRoom] = useState("");
    const [eligibilityType, setEligibilityType] = useState("Cross-country");
    const [eligibilityCountryTags, setEligibilityCountryTags] = useState("");
    const [eligibilityGeoScope, setEligibilityGeoScope] = useState("Cross-border");
    const [eligibilityCrossCountryFlag, setEligibilityCrossCountryFlag] = useState(true);
    const [eligibilityCityLangTags, setEligibilityCityLangTags] = useState("");
    const [eligibilityVisibility, setEligibilityVisibility] = useState("Public");
    const [eligibilityFlag, setEligibilityFlag] = useState(true);
    const [eligibilityResult, setEligibilityResult] = useState(null);
    const [eligibilityLoading, setEligibilityLoading] = useState(false);
    const [roomLookupLoading, setRoomLookupLoading] = useState(false);
    const [roomFoundInDb, setRoomFoundInDb] = useState(null); // null | 'found' | 'not_found'

    // New states for searchable room selection dropdown
    const [roomsList, setRoomsList] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);

    // Helpers to resolve scope & flags automatically based on metadata standards
    const updateGeoScopeAndFlags = (type) => {
        if (type === "Cross-country") {
            setEligibilityGeoScope("Cross-border");
            setEligibilityCrossCountryFlag(true);
        } else if (type === "Country-specific") {
            setEligibilityGeoScope("National");
            setEligibilityCrossCountryFlag(false);
        } else if (type === "City") {
            setEligibilityGeoScope("Local");
            setEligibilityCrossCountryFlag(false);
        } else if (type === "Innovation board") {
            setEligibilityGeoScope("Local");
            setEligibilityCrossCountryFlag(false);
        } else if (type === "Pitch room") {
            setEligibilityGeoScope("Cross-border");
            setEligibilityCrossCountryFlag(false);
        }
    };

    const updateVisibilityFlags = (visibility) => {
        const isEligible = visibility === "Public" || visibility === "Discoverable";
        setEligibilityFlag(isEligible);
    };

    // Debounced room name lookup — fetches matching rooms for dropdown selector
    useEffect(() => {
        if (!eligibilityRoom || eligibilityRoom.trim().length < 1) {
            setRoomsList([]);
            setShowDropdown(false);
            setRoomFoundInDb(null);
            return;
        }
        setRoomLookupLoading(true);
        const timer = setTimeout(async () => {
            try {
                const res = await apiClient.get(`/private/get-rooms?search=${encodeURIComponent(eligibilityRoom)}&limit=10`);
                const rooms = res?.data?.payload?.rooms || [];
                setRoomsList(rooms);
                if (rooms.length > 0) {
                    const exactMatch = rooms.find(r => 
                        (r.name || r.r_name || "").toLowerCase() === eligibilityRoom.trim().toLowerCase()
                    );
                    if (exactMatch) {
                        setRoomFoundInDb('found');
                    } else {
                        setRoomFoundInDb('not_found');
                    }
                } else {
                    setRoomFoundInDb('not_found');
                }
            } catch (e) {
                console.error("Room lookup error:", e);
                setRoomFoundInDb('not_found');
            } finally {
                setRoomLookupLoading(false);
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [eligibilityRoom]);

    // Handle selecting room from search dropdown list
    const handleSelectRoom = (room) => {
        const rName = room.name || room.r_name || "";
        setEligibilityRoom(rName);
        
        const validTypes = ["Cross-country", "Country-specific", "City", "Innovation board", "Pitch room"];
        const rType = room.type || room.r_type || "Cross-country";
        const normalizedType = validTypes.includes(rType) ? rType : "Cross-country";
        setEligibilityType(normalizedType);
        updateGeoScopeAndFlags(normalizedType);
        
        setEligibilityCountryTags(room.country || room.r_country || "");
        setEligibilityCityLangTags(room.city || room.r_city || "");
        
        const rVisibility = room.visibility || room.r_visibility || "Public";
        setEligibilityVisibility(rVisibility);
        updateVisibilityFlags(rVisibility);
        
        const rEligible = room.eligible !== undefined ? room.eligible : (room.r_eligible !== undefined ? room.r_eligible : true);
        setEligibilityFlag(rEligible);
        
        setRoomFoundInDb('found');
        setShowDropdown(false);
    };

    // Real-time client-side validation logic
    useEffect(() => {
        if (!eligibilityRoom) {
            setEligibilityResult(null);
            return;
        }

        const errors = [];
        
        // 1. Room Type classified validation
        if (!eligibilityType) {
            errors.push("Room type must be classified (e.g. cross-country, city, innovation board).");
        }

        // 2. Cross-country tag check (Requires >= 2 country tags and cross-country flag)
        if (eligibilityType === "Cross-country") {
            const cTagList = eligibilityCountryTags
                ? eligibilityCountryTags.split(',').map(t => t.trim().toLowerCase()).filter(t => t)
                : [];
            const uniqueCountries = new Set(cTagList);
            if (uniqueCountries.size < 2) {
                errors.push("Cross-country classification requires 2 or more distinct country tags.");
            }
            if (!eligibilityCrossCountryFlag) {
                errors.push("Cross-Country eligibility flag must be set to true for cross-country rooms.");
            }
        }

        // 3. Geography Scope must match the designated Room Type scope rules
        if (eligibilityType === "Cross-country" && eligibilityGeoScope !== "Cross-border") {
            errors.push("Geography scope must be 'Cross-border' for Cross-country rooms.");
        }
        if (eligibilityType === "Country-specific" && eligibilityGeoScope !== "National") {
            errors.push("Geography scope must be 'National' for Country-specific rooms.");
        }
        if (eligibilityType === "City" && eligibilityGeoScope !== "Local") {
            errors.push("Geography scope must be 'Local' for City rooms.");
        }
        if (eligibilityType === "Innovation board" && eligibilityGeoScope !== "Local") {
            errors.push("Geography scope must be 'Local' for Innovation board rooms.");
        }

        // 4. City / Language Tags populated
        if (!eligibilityCityLangTags || eligibilityCityLangTags.trim() === "") {
            errors.push("Supporting metadata (City Tags / Language Tags) must be populated.");
        }

        // 5. Visibility must be Public or Discoverable
        if (!["Public", "Discoverable"].includes(eligibilityVisibility)) {
            errors.push("Room visibility must be Public or Discoverable for reputation tracking.");
        }

        // 6. Reputation-Eligibility flag
        if (!eligibilityFlag) {
            errors.push("Reputation-Eligibility flag must be explicitly marked as eligible.");
        }

        const isEligible = errors.length === 0;
        const explanation = isEligible
            ? "Room passed all metadata validation checks. Events originating from this room will be counted as valid scoring signals and fed into the reputation score calculation."
            : "Room failed validation: " + errors.join(" ") + " All reputation events from this room will be automatically dropped and excluded from scoring.";

        setEligibilityResult({
            isEligible,
            explanation,
            errors,
            matchedRoom: roomFoundInDb === 'found' ? {
                name: eligibilityRoom,
                storedEligibility: eligibilityFlag
            } : null
        });
    }, [
        eligibilityRoom, 
        eligibilityType, 
        eligibilityCountryTags, 
        eligibilityGeoScope, 
        eligibilityCrossCountryFlag, 
        eligibilityCityLangTags, 
        eligibilityVisibility, 
        eligibilityFlag,
        roomFoundInDb
    ]);

    const handleAction = (action, userObj, event) => {
        if (event) event.preventDefault();

        if (action === "View Details") {
            setSelectedUser(userObj);
            setDrawerOpen(true);
            return;
        }

        if (action === "Adjust Score") {
            Swal.fire({
                title: 'Adjust Trust Score',
                text: `Current Score for ${userObj?.user?.name || 'User'}: ${userObj?.score || 0}`,
                input: 'number',
                inputAttributes: { step: 10, min: 0 },
                inputPlaceholder: 'Enter new score',
                showCancelButton: true,
                confirmButtonColor: '#5e72e4',
                inputValidator: (value) => {
                    if (!value || isNaN(value)) return 'Please enter a valid number!';
                    if (value < 0) return 'Score must be at least 0.';
                }
            }).then((result) => {
                if (result.isConfirmed) {
                    const newScore = parseInt(result.value);
                    const updated = users.map(u =>
                        u.id === userObj.id ? { ...u, score: newScore } : u
                    );
                    setUsers(updated);
                    setDrawerOpen(false);
                    Swal.fire({
                        title: "Updated!",
                        text: `Trust score adjusted successfully.`,
                        icon: "success",
                        timer: 2000,
                        timerProgressBar: true,
                        showConfirmButton: false,
                        toast: true,
                        position: 'top-end'
                    });
                }
            });
            return;
        }

        if (action === "Mark as not suspicious") {
            Swal.fire({
                title: 'Clear Suspicious Status?',
                text: `Are you sure you want to mark ${userObj?.name || 'User'} as not suspicious and clear active flags?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#2dce89',
                cancelButtonColor: '#f5365c',
                confirmButtonText: 'Yes, clear status'
            }).then(async (result) => {
                if (result.isConfirmed) {
                    try {
                        const res = await unflagUserReputation({ userId: userObj.id || userObj._id });
                        if (res.status === true || res.status === 'success') {
                            const updatedU = {
                                ...userObj,
                                suspicious: false,
                                flagReason: ""
                            };
                            setUsers(users.map(u => u.id === userObj.id ? updatedU : u));
                            if (selectedUser && selectedUser.id === userObj.id) {
                                setSelectedUser(updatedU);
                            }
                            Swal.fire({
                                title: "Cleared!",
                                text: "User marked as clear.",
                                icon: "success",
                                timer: 2000,
                                timerProgressBar: true,
                                showConfirmButton: false,
                                toast: true,
                                position: 'top-end'
                            });
                        }
                    } catch (err) {
                        console.error("Unflag error:", err);
                        Swal.fire("Error", err.response?.data?.message || err.message, "error");
                    }
                }
            });
            return;
        }

        Swal.fire({
            title: `${action} User`,
            text: `Are you sure you want to flag ${userObj?.user?.name || 'User'} for suspicious behavior?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#f5365c",
            cancelButtonColor: "#8898aa",
            confirmButtonText: "Yes, Flag User",
        }).then((result) => {
            if (result.isConfirmed) {
                const updated = users.map(u =>
                    u.id === userObj.id ? { ...u, suspicious: true } : u
                );
                setUsers(updated);
                setDrawerOpen(false);
                Swal.fire({
                    title: "Flagged!",
                    text: "User's reputation restricted.",
                    icon: "success",
                    timer: 2000,
                    timerProgressBar: true,
                    showConfirmButton: false,
                    toast: true,
                    position: 'top-end'
                });
            }
        });
    };


    const fetchModerationAudits = async (userId) => {
        setModerationLoading(true);
        try {
            const res = await getReputationAudits(userId);
            if (res.success && res.payload) {
                setModerationAudits(res.payload.audits || []);
            }
        } catch (err) {
            console.error("Failed to load moderation audits:", err);
        } finally {
            setModerationLoading(false);
        }
    };

    const handleModerateAudit = async (auditId, status) => {
        setIsModerating(true);
        try {
            const res = await moderateReputationAudit(auditId, status);
            if (res.success) {
                Swal.fire({
                    title: "Success!",
                    text: `Audit ${status} successfully.`,
                    icon: "success",
                    timer: 2000,
                    showConfirmButton: false,
                    toast: true,
                    position: 'top-end'
                });
                fetchModerationAudits(selectedUser?.id || selectedUser?._id);
                fetchUsers(usersPage); // Refresh main table
            } else {
                Swal.fire("Error", res.message || "Action failed", "error");
            }
        } catch (err) {
            console.error("Moderation error:", err);
            Swal.fire("Error", "Server error", "error");
        } finally {
            setIsModerating(false);
        }
    };

    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .dropdown-menu { z-index: 9999 !important; position: absolute !important; will-change: transform; }
            .btn-group .dropdown-menu { z-index: 9999 !important; }
            .table-responsive { overflow-x: auto; WebkitOverflowScrolling: touch; }
            .table { overflow: visible !important; }
            td, th { overflow: visible !important; }
            .custom-switch .custom-control-label::before { height: 1.5rem; width: 2.75rem; border-radius: 1rem; }
            .custom-switch .custom-control-label::after { width: calc(1.5rem - 4px); height: calc(1.5rem - 4px); border-radius: 1rem; }
            .custom-switch .custom-control-input:checked ~ .custom-control-label::after { transform: translateX(1.25rem); }
            .custom-switch .custom-control-label { padding-top: 2px; }
        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);
    return (
        <>
            <CommonHeader />
            <Container className="mt--7" fluid style={{ position: "relative", zIndex: 1, overflowX: "hidden" }}>

                {/* ── Tabs Navigation ─────────────────────── */}
                <div className="filter-bar" style={{ ...styles.filterBar, display: "flex", gap: "10px", overflowX: "auto" }}>
                    {["Dashboard & Users", "Anti-Gaming & Audits", "Eligibility Checker"].map(tab => (
                        <div
                            key={tab}
                            style={{
                                ...styles.filterPill,
                                ...(activeTab === tab ? styles.filterPillActive : {}),
                            }}
                            onClick={() => setActiveTab(tab)}
                        >
                            <i className={`fas ${tab === 'Dashboard & Users' ? 'fa-chart-pie' : tab === 'Anti-Gaming & Audits' ? 'fa-shield-alt' : 'fa-check-double'}`} />
                            {tab}
                        </div>
                    ))}
                </div>

                <TabContent activeTab={activeTab} className="mt-4">
                    <TabPane tabId="Dashboard & Users">
                        {activeTab === "Dashboard & Users" && (
                            <DashboardUsersTab
                                repStats={repStats}
                                loading={loading}
                                users={users}
                                setUsers={setUsers}
                                usersPage={usersPage}
                                usersTotalPages={usersTotalPages}
                                usersTotal={usersTotal}
                                usersLimit={usersLimit}
                                search={search}
                                setSearch={setSearch}
                                setUsersPage={setUsersPage}
                                filters={filters}
                                setFilters={setFilters}
                                permissions={permissions}
                                fetchUsers={fetchUsers}
                                toggleBadgeModal={toggleBadgeModal}
                                setSelectedUser={setSelectedUser}
                                setDrawerOpen={setDrawerOpen}
                                setModerationModal={setModerationModal}
                                fetchModerationAudits={fetchModerationAudits}
                                setAdjustData={setAdjustData}
                                setAdjustModal={setAdjustModal}
                                setHistoryModal={setHistoryModal}
                                setFlagReason={setFlagReason}
                                setFlagModal={setFlagModal}
                                loadHistory={loadHistory}
                                isHistoryLoading={isHistoryLoading}
                                handleAction={handleAction}
                            />
                        )}
                    </TabPane>

                    <TabPane tabId="Anti-Gaming & Audits">
                        {activeTab === "Anti-Gaming & Audits" && (
                            <AntiGamingAuditsTab selectedUser={selectedUser} />
                        )}
                    </TabPane>

                    <TabPane tabId="Eligibility Checker">
                        {activeTab === "Eligibility Checker" && (
                            <EligibilityCheckerTab />
                        )}
                    </TabPane>
                </TabContent>
            </Container>

            {/* ── Score Explanation Panel / Modal ─────────────────── */}
            {selectedUser && (
                <Modal isOpen={drawerOpen} toggle={() => setDrawerOpen(false)} size="md" centered>
                    <ModalHeader toggle={() => setDrawerOpen(false)} className="bg-secondary border-0 pb-0">
                        <div className="font-weight-bold" style={{ fontSize: "18px" }}>Score Explanation Panel</div>
                    </ModalHeader>
                    <ModalBody className="p-0 bg-secondary border-0 pb-4">
                        <div className="bg-white p-4 mx-4 mt-2 mb-4 shadow-sm" style={{ borderRadius: "12px", border: "1px solid #e9ecef" }}>
                            <div className="text-center">
                                <div style={{ ...styles.avatar, background: selectedUser.user?.avatarColor || "#5e72e4", width: "64px", height: "64px", fontSize: "24px", margin: "0 auto 12px auto" }}>
                                    {selectedUser.user?.name?.charAt(0) || selectedUser.name?.charAt(0) || "U"}
                                </div>
                                <h3 className="mb-0 text-dark">{selectedUser.user?.name || selectedUser.name || "Unknown User"}</h3>
                                <div className="text-muted text-sm">{selectedUser.user?.email || selectedUser.email || "No email"}</div>

                                <div className="mt-3">
                                    <span className="font-weight-bold text-dark" style={{ fontSize: "28px" }}>{selectedUser.score || 0}</span>
                                    <span className="text-muted ml-1">TR</span>
                                </div>
                                <div className="mt-1">{getTierBadge(selectedUser.tier)}</div>

                                <div className="mt-4 pt-3 border-top">
                                    <div className="d-flex justify-content-center gap-3 flex-wrap">
                                        {selectedUser.badges?.filter(b => b.level !== 'locked').map((badge, idx) => {
                                            const effectiveProgress = Math.max(0, (badge.progress?.current || 0) - (badge.penaltyProgress || 0));
                                            const isBadgePenalized = badge.penaltyProgress > 0;

                                            return (
                                                <div key={idx} className="text-center p-2 position-relative" style={{ minWidth: '80px', background: '#f8f9fe', borderRadius: '10px', border: isBadgePenalized ? '1px solid #f5365c' : '1px solid #eef1f8' }}>
                                                    {isBadgePenalized && (
                                                        <i className="ri-error-warning-fill text-danger position-absolute" style={{ top: '-5px', right: '-5px', fontSize: '12px' }} title="Penalized by Admin"></i>
                                                    )}
                                                    <div style={{ fontSize: '24px', marginBottom: '4px' }}>
                                                        {badge.badgeId === 'mentor' ? '📖' : badge.badgeId === 'connector' ? '🤝' : badge.badgeId === 'cross_country' ? '🌍' : badge.badgeId === 'multi_language' ? '🔤' : '🏅'}
                                                    </div>
                                                    <div className="text-xs font-weight-bold text-dark">{badge.badgeId.replace('_', ' ').toUpperCase()}</div>
                                                    <div className="text-xs text-primary font-weight-bold">
                                                        {effectiveProgress} / {badge.progress?.required}
                                                        {isBadgePenalized && <div className="text-danger text-xxs" style={{ fontSize: '8px' }}>(Penalized)</div>}
                                                    </div>
                                                    <Badge color="info" style={{ fontSize: '9px', padding: '2px 6px' }} pill className="text-capitalize">{badge.level}</Badge>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {(!selectedUser.badges || selectedUser.badges.filter(b => b.level !== 'locked').length === 0) && (
                                        <div className="text-muted text-xs">No active badges</div>
                                    )}

                                    {selectedUser.isPenaltyActive && (
                                        <div className="mt-4 text-center">
                                            <Button
                                                color={selectedUser?.security?.isUnderReview ? "danger" : "warning"}
                                                size="sm"
                                                outline
                                                onClick={async () => {
                                                    const { value: reason } = await Swal.fire({
                                                        title: 'Reverse All Penalties',
                                                        text: "This will restore original reputation and badge points.",
                                                        input: 'textarea',
                                                        inputLabel: 'Reason for reversal',
                                                        inputPlaceholder: 'Explain why the penalty is being reversed...',
                                                        showCancelButton: true
                                                    });
                                                    if (reason) {
                                                        try {
                                                            const res = await reverseReputationPenalty(selectedUser.id, reason);
                                                            if (res.status) {
                                                                Swal.fire("Reversed!", "User state restored successfully.", "success");
                                                                const originalVal = selectedUser.originalScore || selectedUser.historicalScore || 40;
                                                                const updatedU = {
                                                                    ...selectedUser,
                                                                    score: originalVal,
                                                                    reputationPenaltyScore: 0,
                                                                    suspicious: false,
                                                                    isPenaltyActive: false,
                                                                    flagReason: ""
                                                                };
                                                                setUsers(users.map(u => u.id === selectedUser.id ? updatedU : u));
                                                                setSelectedUser(updatedU);
                                                                setDrawerOpen(false);
                                                                fetchUsers(usersPage);
                                                            }
                                                        } catch (err) {
                                                            Swal.fire("Error", "Failed to reverse penalty", "error");
                                                        }
                                                    }
                                                }}
                                            >
                                                <i className="fas fa-undo mr-2" />
                                                Reverse All Penalties
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="px-4">
                            <h6 className="text-uppercase text-muted ls-1 mb-3">Contributing Events</h6>
                            <div>
                                {(selectedUser.history || []).map((hist, idx) => (
                                    <div key={idx} style={styles.auditTrailLine}>
                                        <div style={styles.auditDot} />
                                        <div className="text-xs text-muted mb-1">{hist.date}</div>
                                        <div className="text-sm text-dark font-weight-bold" dangerouslySetInnerHTML={{ __html: hist.event?.replace(/([+-]\d+ Score:)/, '<span class="text-primary">$1</span>') || hist.event }}></div>
                                    </div>
                                ))}
                                {(!selectedUser.history || selectedUser.history.length === 0) && (
                                    <div className="text-center text-muted py-4">
                                        No history events available
                                    </div>
                                )}
                            </div>
                        </div>
                    </ModalBody>
                </Modal>
            )}

            {/* Adjust Score Modal */}
            <Modal isOpen={adjustModal} toggle={() => setAdjustModal(false)} centered>
                <ModalHeader toggle={() => setAdjustModal(false)} className="bg-secondary border-0">
                    <div className="font-weight-bold">Adjust Reputation Score</div>
                </ModalHeader>
                <ModalBody>
                    <div className="text-center mb-4">
                        <div className="h4 mb-1">{selectedUser?.user?.name || selectedUser?.name || "User"}</div>
                        <div className="text-muted text-sm">Current Score: <span className="font-weight-bold text-primary">{selectedUser?.score || 0}</span></div>
                    </div>
                    <Form>
                        <FormGroup>
                            <Label className="form-control-label">New Reputation Score</Label>
                            <Input
                                type="number"
                                min="0"
                                value={adjustData.score}
                                onChange={(e) => setAdjustData({ ...adjustData, score: e.target.value })}
                                style={{ borderRadius: "10px" }}
                            />
                        </FormGroup>
                        <FormGroup className="mb-4">
                            <Label className="form-control-label">Reason for Adjustment</Label>
                            <Input
                                type="textarea"
                                rows="3"
                                placeholder="Explain why this adjustment is being made..."
                                value={adjustData.reason}
                                onChange={(e) => setAdjustData({ ...adjustData, reason: e.target.value })}
                                style={{ borderRadius: "10px" }}
                            />
                        </FormGroup>

                    </Form>
                </ModalBody>
                <ModalFooter className="border-0 pt-0">
                    <Button color="link" className="text-muted ml-auto" onClick={() => setAdjustModal(false)}>Cancel</Button>
                    <Button color="primary" style={{ borderRadius: "10px", paddingLeft: "30px", paddingRight: "30px" }}
                        disabled={isAdjusting}
                        onClick={async () => {
                            try {
                                if (!adjustData.reason.trim()) {
                                    Swal.fire("Required", "Please provide a reason for the adjustment", "warning");
                                    return;
                                }
                                setIsAdjusting(true);
                                const currentScore = selectedUser?.score || 0;
                                const newScore = parseInt(adjustData.score);
                                const scoreChange = currentScore - newScore;

                                // Format badge penalties for API
                                const badgeChanges = Object.entries(badgePenalties)
                                    .filter(([_, value]) => value > 0)
                                    .map(([id, value]) => ({ badgeId: id, pointsReduced: parseInt(value) }));

                                const res = await adjustReputation(selectedUser.id, {
                                    scoreChange: scoreChange,
                                    reason: adjustData.reason,
                                    penaltyStatus: "Applied",
                                    badgePenalties: badgeChanges
                                });

                                if (res.status === true || res.status === 'success') {
                                    const updatedU = {
                                        ...selectedUser,
                                        score: newScore,
                                        penaltyStatus: "Applied",
                                        isPenaltyActive: (scoreChange > 0 || badgeChanges.length > 0)
                                    };
                                    setUsers(users.map(u => u.id === selectedUser.id ? updatedU : u));
                                    setSelectedUser(updatedU);
                                    Swal.fire("Success", "Reputation and Badge adjustments applied successfully", "success");
                                    setAdjustModal(false);
                                    setBadgePenalties({ connector: 0, mentor: 0, cross_country: 0, multi_language: 0 });
                                    fetchUsers(usersPage);
                                }
                            } catch (err) {
                                console.error("Score adjustment error:", err);
                                Swal.fire("Error", err.response?.data?.message || err.message, "error");
                            } finally {
                                setIsAdjusting(false);
                            }
                        }}>
                        {isAdjusting ? (
                            <>
                                <i className="fas fa-spinner fa-spin mr-2" />
                                Saving...
                            </>
                        ) : "Save Changes"}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Flag User Modal */}
            <Modal isOpen={flagModal} toggle={() => setFlagModal(false)} centered>
                <ModalHeader toggle={() => setFlagModal(false)} className="bg-secondary border-0">
                    <div className="font-weight-bold text-danger">Mark User as Suspicious</div>
                </ModalHeader>
                <ModalBody>
                    <div className="text-center mb-4">
                        <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3" />
                        <div className="h4 mb-1">Confirm Marking {selectedUser?.user?.name || selectedUser?.name || "User"} as Suspicious</div>
                        <p className="text-muted text-sm px-4">
                            This will mark the user as <span className="text-danger font-weight-bold">suspicious</span> and escalate them for review.
                        </p>
                    </div>
                    <FormGroup>
                        <Label className="form-control-label">Reason for Marking Suspicious</Label>
                        <Input
                            type="textarea"
                            rows="3"
                            placeholder="e.g., Bot behavior detected, unnatural upvote patterns..."
                            value={flagReason}
                            onChange={(e) => setFlagReason(e.target.value)}
                            style={{ borderRadius: "10px" }}
                        />
                    </FormGroup>
                </ModalBody>
                <ModalFooter className="border-0 pt-0">
                    <Button color="link" className="text-muted ml-auto" onClick={() => setFlagModal(false)}>Cancel</Button>
                    <Button
                        color="danger"
                        style={{ borderRadius: "10px" }}
                        disabled={isFlagging}
                        onClick={async () => {
                            if (isFlagging) return;
                            setIsFlagging(true);
                            try {
                                const res = await flagUserReputation({
                                    userId: selectedUser.id,
                                    reason: flagReason
                                });
                                if (res.status === true || res.status === 'success') {
                                    const newFlag = res.payload.newFlag;
                                    const updatedU = {
                                        ...selectedUser,
                                        suspicious: true,
                                        flagReason: newFlag.reason
                                    };
                                    setUsers(users.map(u => u.id === selectedUser.id ? updatedU : u));
                                    setSelectedUser(updatedU);
                                    setFlagModal(false);
                                    Swal.fire({
                                        title: "Marked Suspicious!",
                                        text: "User has been marked as suspicious.",
                                        icon: "success",
                                        timer: 2000,
                                        timerProgressBar: true,
                                        showConfirmButton: false,
                                        toast: true,
                                        position: 'top-end'
                                    });
                                }
                            } catch (err) {
                                console.error("Flag error:", err);
                                Swal.fire("Error", err.response?.data?.message || err.message, "error");
                            } finally {
                                setIsFlagging(false);
                            }
                        }}>
                        {isFlagging ? "Marking..." : "Confirm Mark Suspicious"}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Reputation History Modal */}
            <Modal isOpen={historyModal} toggle={() => setHistoryModal(false)} centered size="lg">
                <ModalHeader toggle={() => setHistoryModal(false)} className="bg-secondary border-0">
                    <div className="font-weight-bold">Reputation History: {selectedUser?.name || "User"}</div>
                </ModalHeader>
                <ModalBody style={{ maxHeight: "600px", overflowY: "auto" }}>
                    <Row>
                        <Col md="12">
                            <h6 className="text-uppercase text-muted ls-1 mb-3">Adjustment History</h6>
                            {historyData.history.length > 0 ? (
                                <>
                                    <div className="timeline-container px-3">
                                        {historyData.history.map((item, idx) => (
                                            <div key={idx} style={styles.auditTrailLine}>
                                                <div style={styles.auditDot} />
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <Badge color={item.actionType === "Penalty" ? "danger" : item.actionType?.includes("Reversal") ? "success" : "info"}>
                                                        {item.actionType}
                                                    </Badge>
                                                    <small className="text-muted">{new Date(item.createdAt).toLocaleString()}</small>
                                                </div>
                                                <div className="font-weight-bold text-sm">
                                                    {item.actionType?.includes("Reversal") ? (
                                                        <span className="text-success"><i className="fas fa-undo-alt mr-1" /> {item.actionType}</span>
                                                    ) : (
                                                        <>
                                                            {item.actionType === "Penalty" ? "-" : "+"}
                                                            {Math.abs(item.adjustmentDetails?.scoreChange || 0)} Points
                                                        </>
                                                    )}
                                                    {item.adjustmentDetails?.resultingScore !== undefined && (
                                                        <span className="ml-2 text-muted font-weight-normal" style={{ fontSize: '11px' }}>
                                                            &rarr; Result: <span className="text-dark font-weight-bold">{item.adjustmentDetails.resultingScore}</span> ({item.adjustmentDetails.resultingTier})
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-muted mb-0">{item.reason}</p>

                                                {item.adjustmentDetails?.badgeChanges?.length > 0 && (
                                                    <div className="mt-2 d-flex flex-wrap gap-1">
                                                        {item.adjustmentDetails.badgeChanges.map((bc, bidx) => (
                                                            <Badge key={bidx} color="secondary" className="text-xs" style={{ fontSize: '10px', background: '#e9ecef', color: '#525f7f', border: '1px solid #dee2e6' }} pill>
                                                                <i className="fas fa-certificate mr-1 text-danger" />
                                                                {bc.badgeId.replace('_', ' ').toUpperCase()}: -{bc.pointsReduced}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                )}

                                                <div className="text-xs font-italic mt-2 text-muted">
                                                    Adjusted by: <span className="text-primary font-weight-bold">{item.adminName || "Admin"}</span> {item.adminEmail && <span className="ml-1 opacity-70">({item.adminEmail})</span>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {adjHistoryTotalPages > 1 && (
                                        <nav aria-label="..." className="mt-3">
                                            <Pagination className="pagination justify-content-center mb-0" listClassName="justify-content-center mb-0" size="sm">
                                                <PaginationItem disabled={adjHistoryPage <= 1}>
                                                    <PaginationLink onClick={(e) => { e.preventDefault(); loadHistory(selectedUser.id, adjHistoryPage - 1, historyPage); }} href="#pablo">
                                                        <i className="fas fa-angle-left" />
                                                    </PaginationLink>
                                                </PaginationItem>
                                                {renderPaginationItems(adjHistoryPage, adjHistoryTotalPages * 5, 5, (p) => loadHistory(selectedUser.id, p, historyPage))}
                                                <PaginationItem disabled={adjHistoryPage >= adjHistoryTotalPages}>
                                                    <PaginationLink onClick={(e) => { e.preventDefault(); loadHistory(selectedUser.id, adjHistoryPage + 1, historyPage); }} href="#pablo">
                                                        <i className="fas fa-angle-right" />
                                                    </PaginationLink>
                                                </PaginationItem>
                                            </Pagination>
                                        </nav>
                                    )}
                                </>
                            ) : (
                                <div className="text-center py-4 text-muted font-italic">No adjustments recorded for this user.</div>
                            )}
                        </Col>

                        <Col md="12" className="mt-4 pt-4 border-top">
                            <h6 className="text-uppercase text-muted ls-1 mb-3">System Snapshots</h6>
                            <Table className="align-items-center table-flush" responsive>
                                <thead className="thead-light">
                                    <tr>
                                        <th>Date</th>
                                        <th>Score</th>
                                        <th>Tier</th>
                                        <th>Badges</th>
                                        <th>Adjusted By</th>
                                        <th className="text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historyData.snapshots.map((snap, idx) => (
                                        <tr key={idx}>
                                            <td className="text-xs">{new Date(snap.createdAt).toLocaleDateString()}</td>
                                            <td className="text-xs">
                                                <div className="text-muted mb-1" style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Effective Score</div>
                                                <div className="font-weight-bold text-primary" style={{ fontSize: '15px', lineHeight: '1' }}>{snap.score - (snap.penaltyScore || 0)}</div>
                                                {snap.penaltyScore > 0 && (
                                                    <div className="text-muted mt-1" style={{ fontSize: '10px', fontWeight: '500' }}>
                                                        {snap.score} <span className="text-danger">-{snap.penaltyScore}</span> <span className="opacity-50">Reflected</span>
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <div className="text-muted mb-1" style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Effective Tier</div>
                                                {getTierBadge(snap.tier)}
                                                {snap.historicalTier && snap.historicalTier !== snap.tier && (
                                                    <div className="text-muted mt-1" style={{ fontSize: '10px', fontWeight: '500' }}>
                                                        {snap.historicalTier} <span className="opacity-50">Reflected</span>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="text-xs">
                                                <div className="d-flex flex-wrap gap-1 align-items-center" style={{ maxWidth: '180px' }}>
                                                    {snap.badges?.filter(b => b.level !== 'locked' || (b.penaltyProgress && b.penaltyProgress > 0)).map((b, idx) => {
                                                        const badgeName = b.badgeId.replace('_', ' ').toUpperCase();
                                                        const badgeIcon = b.badgeId === 'mentor' ? '📖' : b.badgeId === 'connector' ? '🤝' : b.badgeId === 'cross_country' ? '🌍' : b.badgeId === 'multi_language' ? '🔤' : '🏅';
                                                        const isLocked = b.level === 'locked';
                                                        const levelColor = isLocked ? '#8898aa' : (b.level === 'gold' ? '#f1c40f' : b.level === 'silver' ? '#adb5bd' : '#cd7f32');
                                                        const isPenalized = (b.penaltyProgress || 0) > 0;

                                                        return (
                                                            <Badge
                                                                key={idx}
                                                                style={{
                                                                    backgroundColor: isPenalized ? '#f5365c15' : `${levelColor}15`,
                                                                    color: isPenalized ? '#f5365c' : levelColor,
                                                                    border: `1px solid ${isPenalized ? '#f5365c40' : `${levelColor}40`}`,
                                                                    padding: '4px 8px',
                                                                    borderRadius: '6px',
                                                                    fontSize: '9px',
                                                                    fontWeight: '800',
                                                                    display: 'inline-flex',
                                                                    flexDirection: 'column',
                                                                    alignItems: 'center',
                                                                    marginBottom: '3px',
                                                                    minWidth: '100px',
                                                                    textAlign: 'center'
                                                                }}
                                                            >
                                                                <div className="d-flex align-items-center mb-1">
                                                                    <span className="mr-1" style={{ fontSize: '11px' }}>{badgeIcon}</span>
                                                                    {badgeName}
                                                                </div>
                                                                <div className="text-xs" style={{ fontSize: '8px', opacity: 0.9, fontWeight: '600' }}>
                                                                    {b.progress}
                                                                    {isPenalized && <span className="text-danger mx-1">-{b.penaltyProgress}</span>}
                                                                    <span className="mx-1">=</span>
                                                                    <span className="text-dark">{b.progress - (b.penaltyProgress || 0)}</span>
                                                                </div>
                                                            </Badge>
                                                        );
                                                    })}
                                                    {(!snap.badges || snap.badges.length === 0) && <span className="text-muted">None</span>}
                                                </div>
                                            </td>
                                            <td className="text-xs">
                                                <div className="font-weight-bold text-dark">{snap.adminName || "System"}</div>
                                                <div className="text-muted" style={{ fontSize: '9px' }}>{snap.adminEmail}</div>
                                            </td>
                                            <td className="text-right">
                                                <Button
                                                    color={selectedUser?.security?.isUnderReview ? "danger" : "warning"}
                                                    size="sm"
                                                    className="btn-icon-only rounded-circle"
                                                    title={snap.reason?.toLowerCase().includes('reversal') ? "Cannot restore a reversal snapshot" : "Restore this stage"}
                                                    disabled={snap.reason?.toLowerCase().includes('reversal')}
                                                    onClick={async () => {
                                                        const { value: reason } = await Swal.fire({
                                                            title: 'Restore this Stage?',
                                                            text: `Revert user reputation to the state from ${new Date(snap.createdAt).toLocaleString()}.`,
                                                            input: 'textarea',
                                                            inputLabel: 'Reason for stage reversal',
                                                            inputPlaceholder: 'Explain why you are restoring this specific stage...',
                                                            showCancelButton: true
                                                        });
                                                        if (reason) {
                                                            try {
                                                                const res = await reverseReputationPenalty(selectedUser.id, reason, snap._id);
                                                                if (res.status) {
                                                                    Swal.fire("Restored!", "User state reverted successfully.", "success");
                                                                    const isMsgPenalty = snap.reason === "Moderated Message Penalty";
                                                                    const targetPenaltyVal = isMsgPenalty ? 0 : (snap.penaltyScore || 0);
                                                                    const targetScore = isMsgPenalty ? snap.score : (snap.score - targetPenaltyVal);
                                                                    const hasBadgePenalty = snap.badges && snap.badges.some(b => (b.penaltyProgress || 0) > 0);
                                                                    const updatedU = {
                                                                        ...selectedUser,
                                                                        score: targetScore,
                                                                        reputationPenaltyScore: targetPenaltyVal,
                                                                        suspicious: targetPenaltyVal > 0,
                                                                        isPenaltyActive: targetPenaltyVal > 0 || hasBadgePenalty,
                                                                        flagReason: targetPenaltyVal > 0 ? (selectedUser.flagReason || "Moderated Message Penalty") : ""
                                                                    };
                                                                    setUsers(users.map(u => u.id === selectedUser.id ? updatedU : u));
                                                                    setSelectedUser(updatedU);
                                                                    setHistoryModal(false);
                                                                    fetchUsers(usersPage);
                                                                }
                                                            } catch (err) {
                                                                Swal.fire("Error", "Failed to restore stage", "error");
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <i className="fas fa-history" style={{ fontSize: '10px' }} />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    {historyData.snapshots.length === 0 && (
                                        <tr>
                                            <td colSpan="6" className="text-center text-muted py-4 font-italic">No snapshots available for this user.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                            {historyTotalPages > 1 && (
                                <nav aria-label="..." className="mt-4 mb-2">
                                    <Pagination
                                        className="pagination justify-content-center mb-0"
                                        listClassName="justify-content-center mb-0"
                                        size="sm"
                                    >
                                        <PaginationItem disabled={historyPage <= 1}>
                                            <PaginationLink
                                                href="#pablo"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    loadHistory(selectedUser.id, adjHistoryPage, historyPage - 1);
                                                }}
                                                tabIndex="-1"
                                            >
                                                <i className="fas fa-angle-left" />
                                            </PaginationLink>
                                        </PaginationItem>

                                        {renderPaginationItems(historyPage, historyTotalPages * 5, 5, (p) => loadHistory(selectedUser.id, adjHistoryPage, p))}

                                        <PaginationItem disabled={historyPage >= historyTotalPages}>
                                            <PaginationLink
                                                href="#pablo"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    loadHistory(selectedUser.id, adjHistoryPage, historyPage + 1);
                                                }}
                                            >
                                                <i className="fas fa-angle-right" />
                                            </PaginationLink>
                                        </PaginationItem>
                                    </Pagination>
                                </nav>
                            )}
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter className="border-0 pt-0">
                    <Button color="secondary" onClick={() => setHistoryModal(false)}>Close</Button>
                </ModalFooter>
            </Modal>

            {/* All Badges Modal */}
            <Modal isOpen={isBadgeModalOpen} toggle={toggleBadgeModal} centered size="md" contentClassName="border-0 shadow-lg" style={{ borderRadius: '20px' }}>
                <ModalHeader toggle={toggleBadgeModal} className="border-0 pb-0">
                    <div className="d-flex align-items-center">
                        <div className="icon icon-shape bg-primary text-white rounded-circle shadow mr-3" style={{ width: '40px', height: '40px' }}>
                            <i className="fas fa-medal" />
                        </div>
                        <h4 className="mb-0 font-weight-bold">Community Badges</h4>
                    </div>
                </ModalHeader>
                <ModalBody className="pt-4">
                    <div className="text-sm text-muted mb-4">
                        Discover all badges awarded across the community.
                    </div>
                    <div style={{ maxHeight: '400px', overflowY: 'auto', overflowX: 'hidden', paddingRight: '5px' }}>
                        <Row className="m-0">
                            {(repStats?.allBadges || []).map((badge, idx) => {
                                const formattedName = badge?.name
                                    ? badge.name.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())
                                    : "Unknown Badge";
                                return (
                                    <Col xs="12" key={idx} className="mb-3">
                                        <div className="d-flex align-items-center p-3 bg-secondary rounded border border-lighter">
                                            <div className="mr-3 text-primary bg-white rounded-circle shadow-sm d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px' }}>
                                                <i className="fas fa-award" />
                                            </div>
                                            <div className="flex-grow-1">
                                                <div className="font-weight-bold text-dark text-sm">{formattedName}</div>
                                                <div className="text-xs text-muted">Total Awards</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-weight-900 text-primary text-lg">{badge?.count || 0}</div>
                                            </div>
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>
                        {(!repStats?.allBadges || repStats.allBadges.length === 0) && (
                            <div className="text-center py-5">
                                <div className="text-muted mb-3"><i className="fas fa-search fa-3x opacity-3" /></div>
                                <h5 className="font-weight-bold">No Badges Found</h5>
                                <p className="text-sm text-muted">Community members haven't earned any badges yet.</p>
                            </div>
                        )}
                    </div>
                </ModalBody>
                <ModalFooter className="border-0 pt-0">
                    <Button color="secondary" onClick={toggleBadgeModal} className="btn-simple">Close</Button>
                </ModalFooter>
            </Modal>
            {/* 🛡️ REPUTATION MODERATION MODAL */}
            {/* Reputation Moderation Modal */}
            <Modal
                isOpen={moderationModal}
                toggle={() => setModerationModal(false)}
                size="xl"
                centered
                className="modal-dialog-centered"
            >
                <ModalHeader
                    toggle={() => setModerationModal(false)}
                    className="border-0 pb-0"
                >
                    <div>
                        <h3 className="mb-0 text-dark font-weight-bold">
                            <i className="fas fa-shield-alt text-warning mr-2" />
                            Reputation Moderation Center
                        </h3>
                        <p className="text-sm text-muted mb-0 mt-1">
                            Manage trust, penalties, and suspicious activity
                        </p>
                    </div>
                </ModalHeader>

                <ModalBody className="pt-4">
                    {selectedUser && (
                        <>
                            <Row className="mb-4">
                                <Col md="4">
                                    <Card className="shadow-sm border-0">
                                        <CardBody className="text-center">
                                            <div
                                                style={{
                                                    width: "72px",
                                                    height: "72px",
                                                    borderRadius: "50%",
                                                    overflow: "hidden",
                                                    margin: "0 auto 16px",
                                                    border: "4px solid #f4f5f7"
                                                }}
                                            >
                                                <img
                                                    src={getUserImage(selectedUser?.user?.image || selectedUser?.image)}
                                                    alt="avatar"
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover"
                                                    }}
                                                    onError={(e) => {
                                                        e.currentTarget.onerror = null;
                                                        e.currentTarget.src = defaultAvatar;
                                                    }}
                                                />
                                            </div>

                                            <h4 className="mb-1">
                                                {selectedUser?.user?.name || selectedUser?.name || "Unknown User"}
                                            </h4>

                                            <p className="text-muted text-sm mb-3">
                                                {selectedUser?.user?.email || selectedUser?.email || "No email"}
                                            </p>

                                            <Badge
                                                color={
                                                    selectedUser?.suspicious
                                                        ? "danger"
                                                        : "success"
                                                }
                                                pill
                                                className="px-3 py-2"
                                            >
                                                {selectedUser?.suspicious
                                                    ? "FLAGGED ACCOUNT"
                                                    : "TRUSTED USER"}
                                            </Badge>
                                        </CardBody>
                                    </Card>
                                </Col>

                                <Col md="8">
                                    <Row>
                                        <Col md="4">
                                            <div style={styles.analyticsMiniCard}>
                                                <div className="text-muted text-xs text-uppercase font-weight-bold mb-1">
                                                    Reputation Score
                                                </div>

                                                <div
                                                    className="font-weight-900"
                                                    style={{
                                                        fontSize: "28px",
                                                        color:
                                                            selectedUser?.score >= 700
                                                                ? "#2dce89"
                                                                : selectedUser?.score >= 300
                                                                    ? "#fb6340"
                                                                    : "#f5365c"
                                                    }}
                                                >
                                                    {selectedUser?.score || 0}
                                                </div>
                                            </div>
                                        </Col>

                                        <Col md="4">
                                            <div style={styles.analyticsMiniCard}>
                                                <div className="text-muted text-xs text-uppercase font-weight-bold mb-1">
                                                    Trust Score
                                                </div>

                                                <div
                                                    className="font-weight-900 text-info"
                                                    style={{ fontSize: "28px" }}
                                                >
                                                    {selectedUser?.security?.trustScore ?? 0}
                                                </div>
                                            </div>
                                        </Col>

                                        <Col md="4">
                                            <div style={styles.analyticsMiniCard}>
                                                <div className="text-muted text-xs text-uppercase font-weight-bold mb-1">
                                                    Risk Level
                                                </div>

                                                <div
                                                    className="font-weight-900"
                                                    style={{
                                                        fontSize: "28px",
                                                        color:
                                                            (selectedUser?.security?.riskScore || 0) > 70
                                                                ? "#f5365c"
                                                                : (selectedUser?.security?.riskScore || 0) > 40
                                                                    ? "#fb6340"
                                                                    : "#2dce89"
                                                    }}
                                                >
                                                    {selectedUser?.security?.riskScore ?? 0}%
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>

                                    <Card className="mt-4 shadow-sm border-0">
                                        <CardHeader className="bg-white border-0 pb-0">
                                            <h5 className="mb-0">
                                                <i className="fas fa-exclamation-triangle text-danger mr-2" />
                                                Security Flags
                                            </h5>
                                        </CardHeader>

                                        <CardBody>
                                            {(selectedUser?.security?.suspiciousFlags || []).length > 0 ? (
                                                <div className="d-flex flex-wrap">
                                                    {(selectedUser?.security?.suspiciousFlags || []).map((flag, idx) => (
                                                        <Badge
                                                            key={idx}
                                                            color="danger"
                                                            className="mr-2 mb-2 px-3 py-2"
                                                            pill
                                                        >
                                                            <i className="fas fa-bug mr-1" />
                                                            {flag.replace(/_/g, " ")}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-muted">
                                                    <i className="fas fa-check-circle text-success mr-2" />
                                                    No suspicious signals detected
                                                </div>
                                            )}
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>

                            <Row>
                                <Col md="12">
                                    <Card className="shadow-sm border-0 h-100">
                                        <CardHeader className="bg-white">
                                            <div className="d-flex align-items-center justify-content-between">
                                                <h5 className="mb-0">
                                                    <i className="fas fa-history text-primary mr-2" />
                                                    Moderation Audit Trail
                                                </h5>
                                            </div>
                                            <p className="text-muted text-xs mt-2 mb-0">
                                                Review and approve suspicious or pending reputation activity.
                                            </p>
                                        </CardHeader>

                                        <CardBody
                                            style={{
                                                maxHeight: "320px",
                                                overflowY: "auto"
                                            }}
                                        >
                                            {moderationAuditsLoading ? (
                                                <div className="text-center py-4">
                                                    <Spinner color="primary" />
                                                </div>
                                            ) : moderationAudits?.length > 0 ? (
                                                moderationAudits.map((audit, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="d-flex mb-4"
                                                    >
                                                        <div
                                                            className="mr-3"
                                                            style={{
                                                                width: "38px",
                                                                height: "38px",
                                                                borderRadius: "50%",
                                                                background: "#f4f5f7",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center"
                                                            }}
                                                        >
                                                            <i
                                                                className={`fas ${audit.action === "penalty"
                                                                    ? "fa-ban text-danger"
                                                                    : audit.action === "adjustment"
                                                                        ? "fa-sliders-h text-info"
                                                                        : "fa-check text-success"
                                                                    }`}
                                                            />
                                                        </div>

                                                        <div className="flex-grow-1">
                                                            <div className="d-flex align-items-center justify-content-between">
                                                                <div className="font-weight-bold text-dark text-capitalize">
                                                                    {audit.actionType?.replace(/_/g, " ") || audit.action}
                                                                </div>
                                                                <Badge
                                                                    color={
                                                                        audit.status === "approved" ? "success" :
                                                                            audit.status === "rejected" ? "danger" :
                                                                                audit.status === "pending" ? "warning" :
                                                                                    audit.status === "suspicious" ? "danger" : "info"
                                                                    }
                                                                    pill
                                                                    style={{ fontSize: "10px" }}
                                                                >
                                                                    {audit.status}
                                                                </Badge>
                                                            </div>

                                                            <div className="text-sm text-muted">
                                                                {audit.reason || audit.metadata?.flags?.join(", ") || "No reason provided"}
                                                            </div>

                                                            <div className="d-flex align-items-center justify-content-between mt-2">
                                                                <div className="text-xs text-muted">
                                                                    {moment(audit.createdAt).fromNow()}
                                                                </div>

                                                                {audit.status === "pending" && (
                                                                    <div className="d-flex gap-2">
                                                                        <Button
                                                                            color="success"
                                                                            size="sm"
                                                                            className="px-2 py-1"
                                                                            title="Approve"
                                                                            onClick={() => handleModerateAudit(audit._id, "approved")}
                                                                        >
                                                                            <i className="fas fa-check" />
                                                                        </Button>
                                                                        <Button
                                                                            color="danger"
                                                                            size="sm"
                                                                            className="px-2 py-1"
                                                                            title="Reject"
                                                                            onClick={() => handleModerateAudit(audit._id, "rejected")}
                                                                        >
                                                                            <i className="fas fa-times" />
                                                                        </Button>
                                                                        <Button
                                                                            color={selectedUser?.security?.isUnderReview ? "danger" : "warning"}
                                                                            size="sm"
                                                                            className="px-2 py-1"
                                                                            title="Mark as Suspicious"
                                                                            onClick={() => handleModerateAudit(audit._id, "suspicious")}
                                                                        >
                                                                            <i className="fas fa-bug" />
                                                                        </Button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="text-center text-muted py-5">
                                                    No pending or suspicious activity found for this user.
                                                </div>
                                            )}
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                        </>
                    )}
                </ModalBody>
            </Modal>
        </>
    );
};

export default Reputation;